import React, { useEffect, useMemo, useRef } from "react";
import styled from "styled-components";

import MessageItem from "../message/MessageItem";
import ResponseChip from "../response-chip/ResponseChip";
import SoremonActionConfirm from "./SoremonActionConfirm";
import SoremonCharacter from "./SoremonCharacter";
import type { SoremonMood } from "./soremonStore";
import { soremonMoodText } from "./soremonMoodText";
import type { SoremonMessage, SoremonToolUse } from "../../app/services/soremon";
import type { MessageUser } from "../../types/message";
import { soremonMessageToMessageData } from "../../utils/messageAdapters";
import soremonAvatar from "../../assets/images/soremon/soremon.svg";

export interface SoremonMessageListProps {
  messages: SoremonMessage[];
  /** ログイン中のユーザー（自分側の吹き出しの投稿者） */
  me: MessageUser;
  /** 送信中のユーザー発言（応答が返るまで末尾に仮表示する） */
  sendingText?: string | null;
  /** 応答待ち（送信・実行・キャンセルのいずれか） */
  waiting?: boolean;
  /** 応答待ちの間の状態（thinking / acting）と、実行中の操作名 */
  progressMood?: SoremonMood;
  progressLabel?: string;
  onConfirm: (id: number) => void;
  onCancel: (id: number) => void;
  /** 話しかけの例を押したとき */
  onSuggest: (text: string) => void;
}

const SOREMON_AUTHOR: MessageUser = {
  id: "soremon",
  display_name: "soremon",
  avatar: soremonAvatar,
};

/* MessageItem の吹き出しの左端（アバター 32px + gap 4px）にそろえる。 */
const AssistantExtras = styled.div`
  padding-left: 36px;
  max-width: calc(75% + 36px);
`;

const GREETING = "こんにちは、ボクsoremon。\n話してくれたら、進めます。";

const SUGGESTIONS = ["今日やることを教えて", "返信もれしてない？", "スケジュールは大丈夫？"];

const ToolLine: React.FC<{ tool: SoremonToolUse }> = ({ tool }) =>
  tool.risk === "medium" ? (
    <span className="u-row u-items-center u-gap2 u-text-blue">
      <span className="material-symbols-rounded">edit</span>
      {tool.label}しました
    </span>
  ) : (
    <span className="u-row u-items-center u-gap2">
      <span className="material-symbols-rounded">check</span>
      {tool.label}
    </span>
  );

const SoremonMessageList: React.FC<SoremonMessageListProps> = ({
  messages,
  me,
  sendingText,
  waiting = false,
  progressMood = "thinking",
  progressLabel = "",
  onConfirm,
  onCancel,
  onSuggest,
}) => {
  const bottomRef = useRef<HTMLDivElement>(null);
  const authors = useMemo(() => ({ soremon: SOREMON_AUTHOR, me }), [me]);
  const greetingCreated = useMemo(() => new Date().toISOString(), []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "end" });
  }, [messages, sendingText, waiting, progressLabel]);

  const renderItem = (
    message: Pick<SoremonMessage, "id" | "role" | "content" | "created" | "files">,
  ) => (
    <MessageItem
      message={soremonMessageToMessageData(message, authors)}
      isOurs={message.role === "user"}
      showActions={false}
      currentUserId={me.id}
    />
  );

  const isEmpty = messages.length === 0 && !sendingText;

  return (
    <div className="u-col u-items-stretch u-fill u-overflow-y-auto u-pt16 u-pb8 u-pl16 u-pr16">
      {isEmpty && (
        <>
          {renderItem({ id: 0, role: "assistant", content: GREETING, created: greetingCreated })}
          <AssistantExtras className="u-row u-wrap u-gap8 u-pb8">
            {SUGGESTIONS.map((text) => (
              <ResponseChip key={text} onClick={() => onSuggest(text)}>
                {text}
              </ResponseChip>
            ))}
          </AssistantExtras>
        </>
      )}
      {messages.map((message) => (
        <React.Fragment key={message.id}>
          {(message.content || (message.files?.length ?? 0) > 0) && renderItem(message)}
          {message.files
            ?.filter((file) => file.error)
            .map((file) => (
              <div key={file.id} className="bodytext-11 u-text-light-gray u-text-right u-pb8">
                {file.file_name}: {file.error}
              </div>
            ))}
          {message.role === "assistant" &&
            ((message.tools?.length ?? 0) > 0 || (message.actions?.length ?? 0) > 0) && (
              <AssistantExtras className="u-col u-gap8 u-pb8">
                {message.tools && message.tools.length > 0 && (
                  <div className="bodytext-11 u-text-light-gray u-row u-wrap u-gap8">
                    {message.tools.map((tool, index) => (
                      <ToolLine key={`${tool.name}-${index}`} tool={tool} />
                    ))}
                  </div>
                )}
                {message.actions?.map((action) => (
                  <SoremonActionConfirm
                    key={action.id}
                    action={action}
                    disabled={waiting}
                    onConfirm={onConfirm}
                    onCancel={onCancel}
                  />
                ))}
              </AssistantExtras>
            )}
        </React.Fragment>
      ))}
      {sendingText &&
        renderItem({ id: -1, role: "user", content: sendingText, created: new Date().toISOString() })}
      {waiting && (
        <div className="u-row u-items-center u-gap8 u-pb8" aria-live="polite">
          <SoremonCharacter mood={progressMood} size="small" />
          <span className="bodytext-13 u-text-light-gray">
            {soremonMoodText(progressMood, progressLabel)}
          </span>
        </div>
      )}
      <div ref={bottomRef} />
    </div>
  );
};

export default SoremonMessageList;
